// Day-brief command channel — carries `MountChatWidgetHandle.startDayBrief`
// calls into the React tree.
//
// The handle and the widget live in the same bundle and the same mount, so
// this is an instance-scoped channel handed to the widget as a prop rather than
// a `window` bus like pageContextBus/widgetCommandBus (those cross bundles). A
// `window` bus would also leak between mounts and has no way to hold a command
// until the widget subscribes — `mountChatWidget` returns before the widget's
// effects have run, so a host that calls `startDayBrief` right away would lose it.
import { DAY_BRIEF_FAILURE_REASON, DAY_BRIEF_PROTOCOL_VERSION } from '../constants/dayBrief';
export class DayBriefStartError extends Error {
    constructor(reason, retryable) {
        super(`Day brief could not be started: ${reason}`);
        this.name = 'DayBriefStartError';
        this.reason = reason;
        this.retryable = retryable;
    }
}
function validateCommand(command) {
    const candidate = command;
    if (!candidate || typeof candidate !== 'object') {
        return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.INVALID_COMMAND, false);
    }
    if (candidate.protocol_version !== DAY_BRIEF_PROTOCOL_VERSION) {
        return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.UNSUPPORTED_PROTOCOL_VERSION, false);
    }
    if (typeof candidate.client_request_id !== 'string' || candidate.client_request_id.trim() === '') {
        return new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.INVALID_COMMAND, false);
    }
    return null;
}
export function createDayBriefChannel() {
    // Keyed by client_request_id: a host retrying the same command while the first
    // is still pending must not start a second run in the widget.
    const inFlight = new Map();
    const queue = [];
    let consumer = null;
    let active = null;
    let disposed = false;
    const pump = () => {
        if (!consumer || active)
            return;
        const next = queue.shift();
        if (!next)
            return;
        active = next;
        consumer(next);
    };
    const release = (entry) => {
        inFlight.delete(entry.command.client_request_id);
        if (active === entry)
            active = null;
        pump();
    };
    const request = (command) => {
        if (disposed)
            return Promise.reject(new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.WIDGET_DISPOSED, false));
        const invalid = validateCommand(command);
        if (invalid)
            return Promise.reject(invalid);
        const existing = inFlight.get(command.client_request_id);
        if (existing)
            return existing;
        const promise = new Promise((resolve, reject) => {
            let settled = false;
            const entry = {
                command,
                resolve: (result) => {
                    if (settled)
                        return;
                    settled = true;
                    resolve(result);
                    release(entry);
                },
                reject: (error) => {
                    if (settled)
                        return;
                    settled = true;
                    reject(error);
                    release(entry);
                }
            };
            queue.push(entry);
        });
        inFlight.set(command.client_request_id, promise);
        pump();
        return promise;
    };
    const attach = (next) => {
        consumer = next;
        pump();
        return () => {
            if (consumer === next)
                consumer = null;
        };
    };
    const dispose = () => {
        disposed = true;
        consumer = null;
        const pending = active ? [active, ...queue] : [...queue];
        queue.length = 0;
        pending.forEach((entry) => entry.reject(new DayBriefStartError(DAY_BRIEF_FAILURE_REASON.WIDGET_DISPOSED, false)));
    };
    return { request, attach, dispose };
}
